import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function ActiveMockInterview() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { showError } = useToast();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [responseStartTime, setResponseStartTime] = useState(null);
  const [interviewInfo, setInterviewInfo] = useState(null);
  const [resumePdfUrl, setResumePdfUrl] = useState(null);
  const [showResume, setShowResume] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [inCandidateQuestionsPhase, setInCandidateQuestionsPhase] = useState(false);

  // Voice mode state
  const [voiceMode, setVoiceMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const messagesEndRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentAudioRef = useRef(null);

  // Timer
  useEffect(() => {
    timerIntervalRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (resumePdfUrl) {
        URL.revokeObjectURL(resumePdfUrl);
      }
    };
  }, [resumePdfUrl]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load initial message function
  const loadInitialMessage = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      // Try to fetch the interview data to get initial message and resume
      const response = await fetch(`${API_URL}/api/mock-interview/${interviewId}/state`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();

        // Set interview info
        setInterviewInfo(data);

        // Load resume PDF using the same method as Resume Walkthrough Practice
        if (data.application_id) {
          try {
            const pdfResponse = await fetch(`${API_URL}/resume/download/${data.application_id}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });

            if (pdfResponse.ok) {
              const blob = await pdfResponse.blob();
              const url = URL.createObjectURL(blob);
              setResumePdfUrl(url);
            } else {
              setShowResume(false); // Hide resume panel if failed to load
            }
          } catch (err) {
            // Resume load failed silently - interview can proceed without it
          }
        }

        // Load conversation history
        if (data.conversation_history && data.conversation_history.length > 0) {
          const formattedMessages = data.conversation_history.map(msg => ({
            role: msg.role,
            content: msg.content
          }));
          setMessages(formattedMessages);
        }

        // Restore candidate questions phase state if interview was refreshed during that phase
        // ONLY set to true if backend explicitly says we're in candidate_questions state
        if (data.current_state === 'candidate_questions') {
          setInCandidateQuestionsPhase(true);
        } else {
          // Explicitly ensure it's false for other states (defensive)
          setInCandidateQuestionsPhase(false);
        }

        // Set response timer
        setResponseStartTime(Date.now());
        setIsLoading(false);
      } else {
        setResponseStartTime(Date.now());
        setIsLoading(false);
      }
    } catch (error) {
      setResponseStartTime(Date.now());
      setIsLoading(false);
    }
  }, [interviewId, getToken]);

  // Load initial message on mount
  useEffect(() => {
    loadInitialMessage();
  }, [loadInitialMessage]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getResponseTimerColor = (seconds) => {
    if (seconds < 60) return '#22c55e'; // Green
    if (seconds < 90) return '#fbbf24'; // Yellow
    if (seconds < 120) return '#fb923c'; // Orange
    return '#ef4444'; // Red
  };

  const getCurrentResponseTime = () => {
    if (!responseStartTime) return 0;
    return Math.floor((Date.now() - responseStartTime) / 1000);
  };

  // Voice Mode Functions
  const playAudioResponse = async (text) => {
    if (!voiceMode) return;

    try {
      setIsSpeaking(true);
      const token = getToken();

      const response = await fetch(`${API_URL}/api/mock-interview/${interviewId}/tts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });

      if (!response.ok) {
        throw new Error('Failed to generate speech');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;

      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
      };

      await audio.play();
    } catch (error) {
      setIsSpeaking(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      showError('Failed to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    try {
      setIsLoading(true);
      const token = getToken();

      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await fetch(`${API_URL}/api/mock-interview/${interviewId}/stt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to transcribe audio');
      }

      const data = await response.json();
      const transcribedText = data.text.trim();

      if (!transcribedText) {
        showError('No speech detected. Please try again.');
        setIsLoading(false);
        return;
      }

      // Submit directly without showing in text box for cleaner UX
      await submitAnswer(transcribedText);
    } catch (error) {
      showError('Failed to transcribe audio. Please try again.');
      setIsLoading(false);
    }
  };

  const toggleVoiceMode = () => {
    if (voiceMode) {
      // Turning off voice mode - stop any ongoing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      if (isRecording) {
        stopRecording();
      }
    }
    setVoiceMode(!voiceMode);
  };

  // Core submit function that can be called from both text and voice input
  const submitAnswer = async (answerText) => {
    if (!answerText.trim() || isLoading || isCompleted) return;

    const userMessage = answerText.trim();
    const responseDuration = getCurrentResponseTime();

    setIsLoading(true);

    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      duration: responseDuration
    }]);

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/mock-interview/${interviewId}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_answer: userMessage,
          response_duration: responseDuration,
          elapsed_time: elapsedTime
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit response');
      }

      const data = await response.json();

      if (data.is_complete) {
        // Interview completed - mark as completed and show message
        setIsCompleted(true);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Interview complete! Generating your feedback... Please wait.'
        }]);

        // Wait a moment then redirect
        setTimeout(() => {
          navigate(`/mock-interview/${interviewId}/results`);
        }, 2000);
      } else {
        // Add AI's next question
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.next_question.text,
          is_followup: data.is_followup
        }]);

        // Check if we entered the candidate questions phase
        // This enables the "End Interview" button
        if (data.next_question.type === 'candidate_questions') {
          setInCandidateQuestionsPhase(true);
        }

        // Play audio in voice mode
        if (voiceMode) {
          await playAudioResponse(data.next_question.text);
        }

        // Reset response timer
        setResponseStartTime(Date.now());
      }
    } catch (error) {
      showError('Failed to submit response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Wrapper for text input - clears input and calls submitAnswer
  const handleSend = async () => {
    if (!input.trim() || isLoading || isCompleted) return;
    const text = input.trim();
    setInput(''); // Clear input immediately
    await submitAnswer(text);
  };

  const handleExitInterview = async () => {
    if (!window.confirm('Are you sure you want to exit this interview? Your progress will not be saved.')) {
      return;
    }

    try {
      const token = getToken();
      await fetch(`${API_URL}/api/mock-interview/${interviewId}/abandon`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      // Abandon is best-effort - navigate regardless
    }

    navigate('/interview-prep');
  };

  // End Interview gracefully - generates feedback
  const handleEndInterview = async () => {
    if (isLoading || isCompleted) return;

    setIsLoading(true);

    // Add user's end message to UI
    const endMessage = "No more questions, thanks!";
    setMessages(prev => [...prev, {
      role: 'user',
      content: endMessage
    }]);

    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/mock-interview/${interviewId}/respond`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_answer: endMessage,
          response_duration: getCurrentResponseTime(),
          elapsed_time: elapsedTime,
          force_end: true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to end interview');
      }

      const data = await response.json();

      // Interview ended - show closing message and redirect
      setIsCompleted(true);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.next_question?.text || 'Interview complete! Generating your feedback...'
      }]);

      // Wait a moment then redirect to results
      setTimeout(() => {
        navigate(`/mock-interview/${interviewId}/results`);
      }, 2000);
    } catch (error) {
      showError('Failed to end interview. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSend();
    }
  };

  return (
    <div style={{ height: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Fixed Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: '20px 40px',
        borderBottom: '1px solid var(--border-light)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'var(--bg-base)'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', margin: 0, fontWeight: '700' }}>Mock Interview</h1>
          <p style={{ color: 'var(--text-tertiary)', margin: '4px 0 0 0', fontSize: '14px' }}>
            {interviewInfo ? `${interviewInfo.company_name} - ${interviewInfo.job_role}` : 'Answer each question to the best of your ability'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          {resumePdfUrl && (
            <button
              onClick={() => setShowResume(!showResume)}
              style={{
                padding: '8px 16px',
                background: 'rgba(99, 102, 241, 0.2)',
                color: 'var(--indigo-500)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600'
              }}
            >
              {showResume ? 'Hide' : 'Show'} Resume
            </button>
          )}
          <button
            onClick={toggleVoiceMode}
            disabled={isCompleted}
            style={{
              padding: '8px 16px',
              background: voiceMode ? 'rgba(34, 197, 94, 0.2)' : 'rgba(107, 114, 128, 0.2)',
              color: voiceMode ? '#22c55e' : '#9ca3af',
              border: voiceMode ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(107, 114, 128, 0.3)',
              borderRadius: '6px',
              cursor: isCompleted ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              opacity: isCompleted ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {isSpeaking ? '🔊' : '🎤'} {voiceMode ? 'Voice On' : 'Voice Off'}
          </button>
          <button
            onClick={handleExitInterview}
            disabled={isCompleted}
            style={{
              padding: '8px 16px',
              background: 'rgba(239, 68, 68, 0.2)',
              color: '#ef4444',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '6px',
              cursor: isCompleted ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              opacity: isCompleted ? 0.5 : 1
            }}
          >
            Exit Interview
          </button>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
              Interview Time
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {formatTime(elapsedTime)}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>
              Response Time
            </div>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: getResponseTimerColor(getCurrentResponseTime())
            }}>
              {formatTime(getCurrentResponseTime())}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area - Account for fixed header */}
      <div style={{
        marginTop: '108px',
        height: 'calc(100vh - 108px)',
        display: 'flex',
        overflow: 'hidden'
      }}>
        {/* Resume Panel - 40% */}
        {showResume && (
          <div style={{
            width: '40%',
            borderRight: '1px solid var(--border-light)',
            background: 'var(--bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px',
              borderBottom: '1px solid var(--border-light)',
              flexShrink: 0
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: 'var(--indigo-500)' }}>
                Your Resume
              </h3>
            </div>
            <div style={{ flex: 1, overflow: 'auto' }}>
              {resumePdfUrl ? (
                <iframe
                  src={resumePdfUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '100%',
                    border: 'none'
                  }}
                  title="Resume PDF"
                />
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'var(--text-tertiary)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    border: '3px solid var(--border-subtle)',
                    borderTop: '3px solid var(--indigo-500)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  <p>Loading resume...</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Side - Messages + Input - 60% */}
        <div style={{
          width: showResume ? '60%' : '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Messages Container */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '32px'
          }}>
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              marginBottom: '20px',
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
            }}
          >
            <div style={{
              maxWidth: '75%',
              background: msg.role === 'user'
                ? 'rgba(99, 102, 241, 0.2)'
                : 'var(--bg-surface)',
              border: `1px solid ${msg.role === 'user' ? 'rgba(99, 102, 241, 0.3)' : 'var(--border-light)'}`,
              borderRadius: '12px',
              padding: '16px 20px'
            }}>
              <div style={{ fontSize: '15px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {msg.content}
              </div>
              {msg.role === 'user' && msg.duration !== undefined && (
                <div style={{
                  fontSize: '12px',
                  color: getResponseTimerColor(msg.duration),
                  marginTop: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {formatTime(msg.duration)}
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '20px' }}>
            <div style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border-light)',
              borderRadius: '12px',
              padding: '16px 20px',
              color: 'var(--text-tertiary)',
              fontSize: '15px'
            }}>
              AI is processing your response...
            </div>
          </div>
        )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={{
            borderTop: '1px solid var(--border-light)',
            padding: '24px',
            background: 'var(--bg-surface)'
          }}>
            <div style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-light)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={isCompleted ? "Interview completed" : voiceMode ? "Type or use the microphone below..." : "Type your answer... (Cmd/Ctrl + Enter to send)"}
              disabled={isLoading || isCompleted}
              style={{
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                padding: '12px',
                color: 'var(--text-primary)',
                fontSize: '15px',
                resize: 'vertical',
                minHeight: '120px',
                fontFamily: 'inherit',
                lineHeight: '1.6',
                opacity: isCompleted ? 0.5 : 1
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>
                {isCompleted ? 'Generating feedback...' : voiceMode ? 'Voice mode: Click microphone to record your answer' : 'Tip: Be specific, use examples, and quantify your impact'}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                {voiceMode && (
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isLoading || isCompleted || isSpeaking}
                    style={{
                      padding: '12px 24px',
                      background: isRecording
                        ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                        : (isLoading || isCompleted || isSpeaking)
                        ? 'rgba(34, 197, 94, 0.3)'
                        : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      color: 'var(--text-primary)',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (isLoading || isCompleted || isSpeaking) ? 'not-allowed' : 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isRecording ? 'Stop Recording' : isSpeaking ? 'AI Speaking...' : 'Record Answer'}
                  </button>
                )}
                {inCandidateQuestionsPhase && (
                  <button
                    onClick={handleEndInterview}
                    disabled={isLoading || isCompleted}
                    style={{
                      padding: '12px 24px',
                      background: (isLoading || isCompleted)
                        ? 'rgba(20, 184, 166, 0.3)'
                        : 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                      color: 'var(--text-primary)',
                      border: '2px solid rgba(20, 184, 166, 0.5)',
                      borderRadius: '8px',
                      cursor: (isLoading || isCompleted) ? 'not-allowed' : 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      transition: 'all 0.2s',
                      animation: 'pulse-teal 2s ease-in-out infinite',
                      boxShadow: '0 0 15px rgba(20, 184, 166, 0.4)'
                    }}
                  >
                    <style>{`
                      @keyframes pulse-teal {
                        0%, 100% { box-shadow: 0 0 15px rgba(20, 184, 166, 0.4); }
                        50% { box-shadow: 0 0 25px rgba(20, 184, 166, 0.7); }
                      }
                    `}</style>
                    End Interview
                  </button>
                )}
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || isLoading || isCompleted}
                  style={{
                    padding: '12px 32px',
                    background: (!input.trim() || isLoading || isCompleted)
                      ? 'rgba(99, 102, 241, 0.3)'
                      : 'var(--gradient-primary)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: (!input.trim() || isLoading || isCompleted) ? 'not-allowed' : 'pointer',
                    fontSize: '15px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  {isLoading ? 'Sending...' : isCompleted ? 'Completed' : 'Send Answer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
}

export default ActiveMockInterview;
